import React, { useState, useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';
import dayjs from "dayjs";

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledAutocomplete from "@components/inputs/controlledAutocomplete";
import ControlledFilePicker from "@components/inputs/controlledFilePicker";
import ControlledDatePicker from "@components/inputs/controlledDatePicker";
import RemarkDialog from "@components/dialog/remarkDialog";
import CustomTextField from "@components/inputs/customTextField";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints, AssetUrl } from "@configuration/apiEndpoints";

import { formatDateTime } from "@utils/formatter";

import { getFileExtension } from "@utils/helper";

import { ACTION, PAYMENT_TERM,  } from "@utils/constant";


const schema = z.object({
  tinNo: z.string().optional(),
  tinPartyFullName: z.string().optional(),

  paymentMethod: z.object({
    label: z.string().min(1, "Label is required"),
    value: z.string().min(1, "Value is required"),
  }, {invalid_type_error: 'Payment Method is required'}),
  bank: z.object({
    bankName: z.string().min(1, "Label is required"),
    bankGuid: z.string().min(1, "Value is required"),
  }, {invalid_type_error: 'Post Name is required'}),
  paymentDate: z.preprocess((value) => (typeof value === "string" ? new Date(value) : value), z.date({invalid_type_error: 'Payment Date is required'}))
    .refine((date) => !isNaN(date.getTime()), { message: "Invalid date" }),
  document:  z
  .custom((value) => value instanceof File, {
    message: "A valid file is required",
  })
  .refine(
    (file) => file.size <= 5 * 1024 * 1024, // Max size: 5MB
    { message: "File size must be less than 5MB" }
  )
  .refine(
    (file) => ["image/jpeg", "image/png", "application/pdf"].includes(file.type),
    { message: "File type must be JPEG, PNG, or PDF" }
  ),

  // acceptanceNo: z.string().min(1, { message: 'Acceptance No is required' }),

  /* view only */
  postName: z.string().optional(),
  // paymentDate: z.string().optional(),

  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
  paymentNo: z.string().optional(),
  paymentStatus: z.string().optional(),
  paymentGatewayRef: z.string().optional(),

  option: z.string().optional(),
  amount: z.string().optional(),

  createdBy: z.string().optional(),
  createdDate: z.string().optional(),
  verifiedBy: z.string().optional(),
  verifiedDate: z.string().optional(),
  verifiedRemark: z.string().optional(),
  voidBy: z.string().optional(),
  voidDate: z.string().optional(),
  voidRemark: z.string().optional(),
})
.refine(
  (data) => {
    // If one is filled, the other must also be filled
    if (data.tinNo || data.tinPartyFullName) {
      return data.tinNo && data.tinPartyFullName;
    }
    return true; // If both are empty, it's valid
  },
  {
    message: "Both TIN No and Party Full Name must be filled if one is entered.",
    path: ["tinNo"], // The error will be associated with 'tinNo'
  }
);

const PAYMENT_METHOD = {
  ACTIVITY_BANK_TRANSFER: '380824c3-316d-4ba7-85a9-536d17467eeb',
  ACTIVITY_FPX: '8b04cbb7-9340-4d16-b080-19e3e7d9a387', 
  ACTIVITY_CARD: 'beecc75b-873f-451c-bf6e-41561d140d73', 
}

const activityPaymentMethodOptions = [{
  "value": "380824c3-316d-4ba7-85a9-536d17467eeb",
  "label": "Bank Transfer"
}]

export default function ActivityPaymentForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user, isCurrentModuleAdmin } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, formState: { errors } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      paymentMethod: { "value": "380824c3-316d-4ba7-85a9-536d17467eeb", "label": "Bank Transfer" }
    },
  });

  const [validatedData, setValidatedData] = useState(null)
  const [paymentData, setPaymentData] = useState(null)

  const acceptanceNoForm = useForm({})

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [confirmDialogAction, setConfirmDialogAction] = useState(ACTION.SUBMIT)

  const [openRemarkDialog, setOpenRemarkDialog] = useState(false)
  const [remarkDialogAction, setRemarkDialogAction] = useState("")

  useEffect(()=>{
    if (((pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchPaymentMutation.mutate(id)
    }
  },[pageAction])

  const fetchPaymentMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getActivityPayment}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      data.paymentTerm = data?.applications[0]?.post?.paymentTerm
      data.paymentMethod = { value: data?.paymentMethod?.parameterGuid, label: data?.paymentMethod?.valueEng }
      data.tinNo = data?.tinNo || '-'
      data.tinPartyFullName = data?.tinPartyFullName || '-'
      data.postName = data?.applications[0]?.post?.postName
      data.option = data?.applications[0]?.postFeeOption?.optionNameEng
      data.amount = data?.applications[0]?.postFeeOption?.fee
      data.paymentStatus = data?.paymentStatus?.valueEng
      // data.paymentDate = formatDateTime(data?.paymentDate)
      data.createdOn = formatDateTime(data?.createdOn)
      data.verifiedBy = data.verifiedBy || '-'
      data.verifiedDate = data.verifiedDate ? formatDateTime(data.verifiedDate) : '-'
      data.verifiedRemark = data.verifiedRemark || '-'
      data.voidBy = data.voidBy || '-'
      data.voidDate = data.voidDate ? formatDateTime(data.voidDate) : '-'
      data.voidRemark = data.voidRemark || '-'
      data.applicationGuid = data.applications[0]?.applicationGuid
      setPaymentData(data)
      reset(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve payment", "error");
    }
  });


  const bankOptions = useQuery({
    queryKey: ['getBankList'],
    queryFn: () =>
      axios.get(AdminApiEndpoints.getBankList),
    initialData: [],
    select: (res) => {
      return res?.data?.filter(item => item.isActivity === true) || [];
    },
  })

  const validateMutation = useMutation({
    mutationFn: (data) => {
      return axios.get(`${AdminApiEndpoints.validateAcceptanceNo}/${data.acceptanceNo}`)
    },
    onSuccess: (res) => {
      const data = res.data
      setValidatedData(data)
      openSnackbar("Acceptance number validated!", "success");
    },
    onError: (err) => {
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Unable to validate acceptance number", "error");
    }
  })

  const createPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createActivityPayment, data, {headers: {"Content-Type": "multipart/form-data"}})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getActivityPayment'] })
      openSnackbar("Successfully created payment", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create payment", "error");
    }
  })

  const verifyPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.verifyActivityPayment}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getActivityPayment'] })
      openSnackbar("Successfully verified payment", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to verify payment", "error");
    }
  })

  const voidPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.voidActivityPayment}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getActivityPayment'] })
      openSnackbar("Successfully voided payment", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to void payment", "error");
    }
  })

  const rejectPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.rejectActivityPayment}/${id}`, data)
    },
    onSuccess: () => {
      setOpenRemarkDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getActivityPayment'] })
      openSnackbar("Successfully rejected payment", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenRemarkDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to reject payment", "error");
    }
  })

  const invalidPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.invalidActivityPayment}/${id}`, data)
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getActivityPayment'] })
      openSnackbar("Successfully invalided payment", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to invalid payment", "error");
    }
  })

  const refundedPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.refundedActivityPayment}/${id}`, data)
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getActivityPayment'] })
      openSnackbar("Successfully refunded payment", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to refund payment", "error");
    }
  })

  const onValidateAcceptNo = (data) => {
    validateMutation.mutate(data)
  }

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    if(confirmDialogAction === ACTION.SUBMIT){
      const data = getValues();
      const formData = new FormData()
      formData.append('applicationGuid', validatedData?.applicationGuid)
      formData.append('paymentMethodGuid', data?.paymentMethod.value)
      formData.append('bankGuid', data?.bank?.bankGuid)
      formData.append('tinNo', data?.tinNo)
      formData.append('tinPartyFullName', data?.tinPartyFullName)
      formData.append('tinPartyFullName', data?.tinPartyFullName)
      formData.append('paymentDate', dayjs(data.paymentDate).format("YYYY-MM-DD"))
      formData.append('by', user?.username)
      formData.append('payslip', data?.document)
      if(pageAction === "create")
        createPaymentMutation.mutate(formData)
    }
    else if(confirmDialogAction === ACTION.INVALID){
      invalidPaymentMutation.mutate({userGuid: user?.userGuid})
    }
    else if(confirmDialogAction === ACTION.REFUNDED){
      refundedPaymentMutation.mutate({userGuid: user?.userGuid})
    }
  }

  const onConfirmRemark = (data) => {
    if(remarkDialogAction === ACTION.VERIFY)
      verifyPaymentMutation.mutate({verifiedRemark: data?.remark, userGuid: user?.userGuid})
    else if(remarkDialogAction === ACTION.VOID)
      voidPaymentMutation.mutate({voidRemark: data?.remark, userGuid: user?.userGuid})
    else if(remarkDialogAction === ACTION.REJECT)
      rejectPaymentMutation.mutate({rejectRemark: data?.remark, userGuid: user?.userGuid})
  }

  const onBack = () => {
    navigate('/admin/activity-payment', { replace: true });
  }

  const watchAcceptNo = acceptanceNoForm.watch('acceptanceNo')

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (!fetchPaymentMutation.isPending && !fetchPaymentMutation.isError && ["view"].includes(pageAction)) && isCurrentModuleAdmin &&
          <div style={{display: 'flex', gap: "10px"}}>
            {paymentData?.paymentTerm === PAYMENT_TERM.POSTPAYMENT ?
              <>
              <CustomButton color="#4DB6AC" disabled={paymentData?.paymentStatus !== "Submitted"} onClick={()=>{setRemarkDialogAction(ACTION.VERIFY);setOpenRemarkDialog(true)}}>
                Verify
              </CustomButton>
              <CustomButton color="#D13F58" disabled={paymentData?.createdBy === "SYS" || paymentData?.paymentStatus === "Voided"} onClick={()=>{setRemarkDialogAction(ACTION.VOID);setOpenRemarkDialog(true)}}>
                Void
              </CustomButton>
              </>
            : ( paymentData?.paymentTerm === PAYMENT_TERM.PREPAYMENT && paymentData?.paymentMethod?.value === PAYMENT_METHOD.ACTIVITY_BANK_TRANSFER ) ?
              <>
              <CustomButton color="#4DB6AC" disabled={paymentData?.paymentStatus !== "Submitted"} onClick={()=>{setRemarkDialogAction(ACTION.VERIFY);setOpenRemarkDialog(true)}}>
                Verify
              </CustomButton>
              <CustomButton color="#D13F58" disabled={paymentData?.paymentStatus !== "Submitted"} onClick={()=>{setRemarkDialogAction(ACTION.REJECT);setOpenRemarkDialog(true)}}>
                Reject
              </CustomButton>
              <CustomButton color="#EE685B" disabled={paymentData?.paymentStatus !== "Submitted"} onClick={()=>{setConfirmDialogAction(ACTION.INVALID);setOpenConfirmDialog(true)}}>
                Invalid
              </CustomButton>
              <CustomButton disabled={!["Pending Refund"].includes(paymentData?.paymentStatus ?? '')} onClick={()=>{setConfirmDialogAction(ACTION.REFUNDED);setOpenConfirmDialog(true)}}>
                Refunded
              </CustomButton>
              </>
            : ( paymentData?.paymentTerm === PAYMENT_TERM.PREPAYMENT && [PAYMENT_METHOD.ACTIVITY_CARD, PAYMENT_METHOD.ACTIVITY_FPX].includes(paymentData?.paymentMethod?.value) ) ?
              <>
                <CustomButton color="#D13F58" disabled={paymentData?.paymentStatus !== "Paid"} onClick={()=>{setRemarkDialogAction(ACTION.REJECT);setOpenRemarkDialog(true)}}>
                  Reject
                </CustomButton>
                <CustomButton disabled={!["Pending Refund"].includes(paymentData?.paymentStatus ?? '')} onClick={()=>{setConfirmDialogAction(ACTION.REFUNDED);setOpenConfirmDialog(true)}}>
                  Refunded
                </CustomButton>
              </>
            : null
            }
          </div>
        }
      />
      <SkeletonWrapper isLoading={fetchPaymentMutation.isPending} isError={fetchPaymentMutation.isError} errorMessage="Unable to load payment" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        
        {["create"].includes(pageAction) && <>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Payment Details</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="acceptanceNo"
                control={acceptanceNoForm.control}
                label="Acceptance No"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction) || validatedData}
              />
            </Grid2>
            {!validatedData ? 
              <Grid2 size={12} display={'flex'} justifyContent={"flex-end"} gap={"10px"}>
                <CustomButton onClick={acceptanceNoForm.handleSubmit(onValidateAcceptNo)} disabled={!watchAcceptNo}>Validate Acceptance No</CustomButton>
              </Grid2>
              :
              <>
              <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                <CustomTextField 
                  value={validatedData?.post?.postName || '-'}
                  label={'Post Name'}
                  disabled={true}
                />
              </Grid2>
              <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                <CustomTextField 
                  value={validatedData?.postFeeOption?.optionNameEng || '-'}
                  label={'Option'}
                  disabled={true}
                />
              </Grid2>
              <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                <CustomTextField 
                  value={validatedData?.postFeeOption?.fee || '-'}
                  label={'Amount (RM)'}
                  disabled={true}
                />
              </Grid2>
              </>
            }
            
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Additional Details</Typography>
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="tinNo"
                control={control}
                label="TIN No"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="tinPartyFullName"
                control={control}
                label="TIN Party Fullname"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>
        </>}

        {["view"].includes(pageAction) && <>
        
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
            }}
          >
            <Grid2 container spacing={"20px"}>
              <Grid2 size={12}>
                <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Payment Details</Typography>
              </Grid2>

              <Grid2 size={12}>
                <ControlledTextField 
                  name="postName"
                  control={control}
                  label="Post Name"
                  disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name="tinNo"
                  control={control}
                  label="Tin No"
                  disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name="tinPartyFullName"
                  control={control}
                  label="TIN Party Fullname"
                  disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={12} display={'flex'} justifyContent={'flex-end'} gap={'10px'}>
                <CustomButton color="#007BFF" component={RouterLink} to={`/admin/applications/view?id=${paymentData?.applicationGuid}`} target="_blank" rel="noopener noreferrer">
                  View Application
                </CustomButton>
                <CustomButton 
                  color="#6C757D" 
                  disabled={!paymentData?.payslipPath} 
                  component="a" 
                  href={`${AssetUrl}${paymentData?.payslipPath}`} 
                  download={`${paymentData?.paymentNo}.${getFileExtension(paymentData?.payslipPath)}`}
                  target="_blank"
                rel="noopener noreferrer"
                >
                  Download Slip
                </CustomButton>
              </Grid2>
            </Grid2>
          </Paper>

          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
            }}
          >
            <Grid2 container spacing={"20px"}>
              <Grid2 size={12}>
                <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Campaign Details</Typography>
              </Grid2>

              <Grid2 size={12}>
                <ControlledTextField 
                  name="postName"
                  control={control}
                  label="Post Name"
                  disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name="option"
                  control={control}
                  label="Option"
                  disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>

              <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
                <ControlledTextField 
                  name="amount"
                  control={control}
                  label="Amount"
                  disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                  readOnly={["view"].includes(pageAction)}
                />
              </Grid2>
            </Grid2>
          </Paper>

        </>}

        

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Payment Method</Typography>
            </Grid2>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="paymentMethod"
                control={control}
                label="Payment Method"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                options={activityPaymentMethodOptions}
                // loading={activityPaymentMethodOptions.isFetching}
                disableClearable
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledAutocomplete 
                name="bank"
                control={control}
                label="Bank"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                options={bankOptions.data}
                loading={bankOptions.isFetching}
                getOptionLabel={(option) => `${option.bankName} - ${option.accountNumber}`}
                getOptionKey={(option) => option.bankGuid}
                isOptionEqualToValue={(option, value) => option.bankGuid === value?.bankGuid}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledDatePicker 
                name="paymentDate"
                control={control}
                label="Payment Date"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
              />
            </Grid2>

            {["create"].includes(pageAction) && <>
            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledFilePicker 
                name="document"
                control={control}
                label="Upload Document"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || ["view"].includes(pageAction)}
                type="file"
              />
            </Grid2>
            </>}

            {["view"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentNo"
                control={control}
                label="Payment No"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentStatus"
                control={control}
                label="Payment Status"
                disabled={true}
              />
            </Grid2>

            {/* <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentDate"
                control={control}
                label="Payment Date"
                disabled={true}
              />
            </Grid2> */}

            {/* <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentGatewayRef"
                control={control}
                label="Payment Gateway Ref"
                disabled={true}
              />
            </Grid2> */}
            </>}

          </Grid2>
        </Paper>
        {["view"].includes(pageAction) && <>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Other Details</Typography>
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created Date"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="verifiedBy"
                control={control}
                label="Verified By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="verifiedDate"
                control={control}
                label="Verified Date"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="verifiedRemark"
                control={control}
                label="Verified Remark"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="voidBy"
                control={control}
                label="Void By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="voidDate"
                control={control}
                label="Void Date"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="voidRemark"
                control={control}
                label="Void Remark"
                readOnly={true}
              />
            </Grid2>

          </Grid2>
        </Paper>
        </>}
        {["create"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError || !validatedData}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription={
          confirmDialogAction === ACTION.SUBMIT ?
          'Submit payment?'
          : confirmDialogAction === ACTION.INVALID ?
          'Invalid payment?'
          : confirmDialogAction === ACTION.REFUNDED ?
          'Refunded payment?'
          : ''
        }
        onSubmit={()=>onConfirmSubmit()}
        loading={createPaymentMutation.isPending || invalidPaymentMutation.isPending || refundedPaymentMutation.isPending}
      />
      <RemarkDialog 
        open={openRemarkDialog}
        onClose={()=>setOpenRemarkDialog(false)}
        dialogTitle={`${remarkDialogAction} Application`}
        onSubmit={onConfirmRemark}
        loading={verifyPaymentMutation.isPending || voidPaymentMutation.isPending || rejectPaymentMutation.isPending}
      />
    </div>
  );
}
